import React, { useState, useEffect } from 'react';
import { FileText, ThumbsUp, ThumbsDown, ExternalLink, Plus, X, Send, AlertTriangle, CheckCircle, Link as LinkIcon, ShieldCheck, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// List of reputable/verified news and fact-checking sources
const REPUTABLE_SOURCES = {
  // Major News Organizations
  'reuters.com': { name: 'Reuters', verified: true, type: 'news' },
  'apnews.com': { name: 'Associated Press', verified: true, type: 'news' },
  'bbc.com': { name: 'BBC', verified: true, type: 'news' },
  'bbc.co.uk': { name: 'BBC', verified: true, type: 'news' },
  'nytimes.com': { name: 'NY Times', verified: true, type: 'news' },
  'washingtonpost.com': { name: 'Washington Post', verified: true, type: 'news' },
  'theguardian.com': { name: 'The Guardian', verified: true, type: 'news' },
  'wsj.com': { name: 'Wall Street Journal', verified: true, type: 'news' },
  'npr.org': { name: 'NPR', verified: true, type: 'news' },
  'pbs.org': { name: 'PBS', verified: true, type: 'news' },
  'cnn.com': { name: 'CNN', verified: true, type: 'news' },
  'nbcnews.com': { name: 'NBC News', verified: true, type: 'news' },
  'cbsnews.com': { name: 'CBS News', verified: true, type: 'news' },
  'abcnews.go.com': { name: 'ABC News', verified: true, type: 'news' },
  'economist.com': { name: 'The Economist', verified: true, type: 'news' },
  'ft.com': { name: 'Financial Times', verified: true, type: 'news' },
  'bloomberg.com': { name: 'Bloomberg', verified: true, type: 'news' },
  
  // Fact-Checking Organizations
  'snopes.com': { name: 'Snopes', verified: true, type: 'factcheck' },
  'politifact.com': { name: 'PolitiFact', verified: true, type: 'factcheck' },
  'factcheck.org': { name: 'FactCheck.org', verified: true, type: 'factcheck' },
  'fullfact.org': { name: 'Full Fact', verified: true, type: 'factcheck' },
  'checkyourfact.com': { name: 'Check Your Fact', verified: true, type: 'factcheck' },
  'leadstories.com': { name: 'Lead Stories', verified: true, type: 'factcheck' },
  
  // Scientific/Academic Sources
  'nature.com': { name: 'Nature', verified: true, type: 'science' },
  'science.org': { name: 'Science', verified: true, type: 'science' },
  'nih.gov': { name: 'NIH', verified: true, type: 'gov' },
  'cdc.gov': { name: 'CDC', verified: true, type: 'gov' },
  'who.int': { name: 'WHO', verified: true, type: 'gov' },
  'pubmed.ncbi.nlm.nih.gov': { name: 'PubMed', verified: true, type: 'science' },
  'scholar.google.com': { name: 'Google Scholar', verified: true, type: 'science' },
  'arxiv.org': { name: 'arXiv', verified: true, type: 'science' },
  
  // Government Sources
  'gov.uk': { name: 'UK Gov', verified: true, type: 'gov' },
  'usa.gov': { name: 'USA.gov', verified: true, type: 'gov' },
  'europa.eu': { name: 'EU', verified: true, type: 'gov' },
  'un.org': { name: 'United Nations', verified: true, type: 'gov' },
  
  // Reference
  'wikipedia.org': { name: 'Wikipedia', verified: false, type: 'reference' },
  'britannica.com': { name: 'Britannica', verified: true, type: 'reference' },
};

const getSourceInfo = (url) => {
  try {
    const hostname = new URL(url).hostname.replace('www.', '');
    // Check exact match first
    if (REPUTABLE_SOURCES[hostname]) {
      return { ...REPUTABLE_SOURCES[hostname], hostname };
    }
    // Check if it's a subdomain of a reputable source
    for (const [domain, info] of Object.entries(REPUTABLE_SOURCES)) {
      if (hostname.endsWith(`.${domain}`) || hostname === domain) {
        return { ...info, hostname };
      }
    }
    return { name: hostname, verified: false, type: 'unknown', hostname };
  } catch {
    return { name: url, verified: false, type: 'unknown', hostname: url };
  }
};

const SourceBadge = ({ url }) => {
  const source = getSourceInfo(url);
  
  const typeColors = {
    factcheck: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
    news: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    science: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    gov: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    reference: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600',
    unknown: 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  };
  
  const typeLabels = {
    factcheck: 'Fact-Check',
    news: 'News',
    science: 'Scientific',
    gov: 'Government',
    reference: 'Reference',
    unknown: 'Source',
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all hover:scale-105 hover:shadow-md ${typeColors[source.type]}`}
      data-testid={`source-${source.hostname}`}
    >
      {source.verified ? (
        <ShieldCheck className="w-3.5 h-3.5" />
      ) : (
        <Globe className="w-3.5 h-3.5" />
      )}
      <span>{source.name}</span>
      {source.verified && (
        <span className="text-[10px] opacity-70">✓ {typeLabels[source.type]}</span>
      )}
      <ExternalLink className="w-3 h-3 opacity-50" />
    </a>
  );
};

// Component to display community notes on a post
const CommunityNoteDisplay = ({ note, onVote, postId, onReplyAdded, depth = 0 }) => {
  const [voting, setVoting] = useState(false);
  const [showReply, setShowReply] = useState(false);

  const handleVote = async (voteType) => {
    if (voting) return;
    setVoting(true);
    try {
      await onVote(note.note_id, voteType);
    } finally {
      setVoting(false);
    }
  };

  const maxDepth = 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${depth === 0 ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' : 'bg-amber-100/50 dark:bg-amber-800/15 border border-amber-300/50 dark:border-amber-700/40'} rounded-xl p-4 ${depth > 0 ? 'ml-6 mt-2' : 'mt-3'}`}
      data-testid={`community-note-${note.note_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 ${depth === 0 ? 'bg-amber-100 dark:bg-amber-800/50' : 'bg-amber-200/60 dark:bg-amber-700/40'} rounded-full`}>
          <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
              {depth > 0 ? 'Reply Note' : 'Community Note'}
            </span>
            {note.is_visible && (
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="w-3 h-3" /> Rated Helpful
              </span>
            )}
          </div>
          
          <p className="text-sm text-gray-800 dark:text-gray-200 mb-3">
            {note.content}
          </p>
          
          {/* Sources with reputation badges */}
          {note.sources && note.sources.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 mb-2">Sources:</p>
              <div className="flex flex-wrap gap-2">
                {note.sources.map((source, idx) => (
                  <SourceBadge key={idx} url={source} />
                ))}
              </div>
            </div>
          )}
          
          {/* Author & Voting */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Avatar className="w-5 h-5">
                <AvatarImage src={note.author?.avatar} />
                <AvatarFallback className="text-xs bg-amber-200 text-amber-800">
                  {note.author?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-gray-500">
                Added by {note.author?.name} · {note.created_at ? formatDistanceToNow(new Date(note.created_at), { addSuffix: true }) : 'just now'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {depth < maxDepth && (
                <button
                  onClick={() => setShowReply(!showReply)}
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs hover:bg-amber-100 dark:hover:bg-amber-800/30 text-amber-600 dark:text-amber-400 transition-colors"
                  data-testid={`reply-note-${note.note_id}`}
                >
                  <Plus className="w-3 h-3" />
                  Note this
                </button>
              )}
              <button
                onClick={() => handleVote('helpful')}
                disabled={voting}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                  note.user_vote === 'helpful'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
                data-testid={`vote-helpful-${note.note_id}`}
              >
                <ThumbsUp className="w-3.5 h-3.5" />
                {note.helpful_votes}
              </button>
              <button
                onClick={() => handleVote('not_helpful')}
                disabled={voting}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                  note.user_vote === 'not_helpful'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
                data-testid={`vote-not-helpful-${note.note_id}`}
              >
                <ThumbsDown className="w-3.5 h-3.5" />
                {note.not_helpful_votes}
              </button>
            </div>
          </div>

          {/* Reply form */}
          <AnimatePresence>
            {showReply && (
              <AddCommunityNote
                postId={postId}
                parentNoteId={note.note_id}
                onNoteAdded={() => { setShowReply(false); onReplyAdded?.(); }}
                onClose={() => setShowReply(false)}
                isReply
              />
            )}
          </AnimatePresence>

          {/* Nested replies */}
          {note.replies && note.replies.length > 0 && (
            <div className="mt-2">
              {note.replies.map(reply => (
                <CommunityNoteDisplay
                  key={reply.note_id}
                  note={reply}
                  onVote={onVote}
                  postId={postId}
                  onReplyAdded={onReplyAdded}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Component to add a new community note
const AddCommunityNote = ({ postId, onNoteAdded, onClose, parentNoteId = null, isReply = false }) => {
  const [content, setContent] = useState('');
  const [sources, setSources] = useState(['']);
  const [submitting, setSubmitting] = useState(false);

  const addSourceField = () => {
    if (sources.length < 5) {
      setSources([...sources, '']);
    }
  };

  const updateSource = (idx, value) => {
    const newSources = [...sources];
    newSources[idx] = value;
    setSources(newSources);
  };

  const removeSource = (idx) => {
    setSources(sources.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please add a note');
      return;
    }

    setSubmitting(true);
    try {
      const validSources = sources.filter(s => s.trim() && isValidUrl(s.trim()));
      
      await axios.post(`${API_URL}/posts/${postId}/community-notes`, {
        content: content.trim(),
        sources: validSources.length > 0 ? validSources : [],
        parent_note_id: parentNoteId
      }, { withCredentials: true });
      
      toast.success(isReply ? 'Reply note submitted!' : 'Community note submitted! It will be shown once it receives enough helpful votes.');
      onNoteAdded?.();
      onClose?.();
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        toast.info('You have already added a note to this post');
      } else {
        toast.error('Failed to submit note');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mt-3"
      data-testid="add-community-note"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-500" />
          <span className="font-bold text-blue-700 dark:text-blue-300">{isReply ? 'Note This Note' : 'Add Community Note'}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
        {isReply ? 'Add context, corrections, or counter-arguments to this community note.' : 'Help others understand the context of this post. Add fact-checks, corrections, or additional information.'}
      </p>
      
      <Textarea
        placeholder={isReply ? "Reply with your perspective..." : "Write your fact-check or clarification..."}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="rounded-xl resize-none mb-3 bg-white dark:bg-gray-800"
        rows={isReply ? 2 : 3}
        data-testid="note-content"
      />
      
      {/* Sources */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
            Sources (optional, but recommended)
          </span>
          {sources.length < 5 && (
            <button
              onClick={addSourceField}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add source
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {sources.map((source, idx) => (
            <div key={idx} className="flex gap-2">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  placeholder="https://..."
                  value={source}
                  onChange={(e) => updateSource(idx, e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              {sources.length > 1 && (
                <button
                  onClick={() => removeSource(idx)}
                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <Button
        onClick={handleSubmit}
        disabled={submitting || !content.trim()}
        className="w-full rounded-full bg-blue-500 hover:bg-blue-600 text-white"
        data-testid="submit-note"
      >
        {submitting ? 'Submitting...' : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Submit Note for Review
          </>
        )}
      </Button>
    </motion.div>
  );
};

// Main component that wraps post content with community notes
const CommunityNotes = ({ postId, hasReports = false }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [postId]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/community-notes`, {
        withCredentials: true
      });
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (noteId, voteType) => {
    try {
      await axios.post(`${API_URL}/posts/${postId}/community-notes/${noteId}/vote?vote=${voteType}`, {}, { withCredentials: true });
      
      // Update local state
      setNotes(notes.map(n => {
        if (n.note_id === noteId) {
          const wasHelpful = n.user_vote === 'helpful';
          const wasNotHelpful = n.user_vote === 'not_helpful';
          
          return {
            ...n,
            user_vote: voteType,
            helpful_votes: n.helpful_votes + (voteType === 'helpful' ? 1 : 0) - (wasHelpful ? 1 : 0),
            not_helpful_votes: n.not_helpful_votes + (voteType === 'not_helpful' ? 1 : 0) - (wasNotHelpful ? 1 : 0)
          };
        }
        return n;
      }));
      
      toast.success(`Voted as ${voteType.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  // Filter visible notes (those with enough helpful votes or approved)
  const visibleNotes = notes.filter(n => n.status === 'approved' || n.helpful_votes >= 3);
  const hiddenNotes = notes.filter(n => n.status !== 'approved' && n.helpful_votes < 3);

  if (loading) return null;
  
  // Don't show anything if no notes and no reports
  if (notes.length === 0 && !hasReports && !showAddNote) return null;

  return (
    <div className="mt-3" data-testid="community-notes-section">
      {/* Show visible notes */}
      {visibleNotes.map(note => (
        <CommunityNoteDisplay key={note.note_id} note={note} onVote={handleVote} postId={postId} onReplyAdded={fetchNotes} />
      ))}
      
      {/* Show hidden notes toggle */}
      {hiddenNotes.length > 0 && (
        <button
          onClick={() => setShowAllNotes(!showAllNotes)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          {showAllNotes ? 'Hide' : 'Show'} {hiddenNotes.length} pending note{hiddenNotes.length > 1 ? 's' : ''}
        </button>
      )}
      
      {showAllNotes && hiddenNotes.map(note => (
        <CommunityNoteDisplay key={note.note_id} note={note} onVote={handleVote} postId={postId} onReplyAdded={fetchNotes} />
      ))}
      
      {/* Add note section */}
      <AnimatePresence>
        {showAddNote && (
          <AddCommunityNote
            postId={postId}
            onNoteAdded={fetchNotes}
            onClose={() => setShowAddNote(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Add note button */}
      {!showAddNote && (
        <button
          onClick={() => setShowAddNote(true)}
          className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          data-testid="add-note-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Add a community note
        </button>
      )}
    </div>
  );
};

export default CommunityNotes;
export { CommunityNoteDisplay, AddCommunityNote };
